"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { useClientReady } from "@/hooks/useClientReady";
import type React from "react";
import pricingService from "@/services/pricing";
import { TarotApiService } from "@/services/tarotApi";
import { trackLandingCtaClick, trackPageView } from "@/services/analytics";
import { appendUtmToUrl, collectUtmFromLocation } from "@/lib/tracking";
import { isInstagramInApp } from "@/lib/inAppBrowser";
import { config } from "@/config/app";
import {
  addStartupMark,
  isRuntimeSafeModeEnabled,
} from "@/lib/runtimeDiagnostics";
import { getBaseUrlByLanguage } from "@/lib/seo";

/*
 * Paleta da landing (místico/premium) — manter alinhada com HomeClient.tsx (pt-BR):
 *   títulos ........ #5c1616  (bordô profundo)
 *   corpo .......... #4a3230  (marrom quente)
 *   secundário ..... #8a6a5f  (marrom acinzentado)
 *   dourado claro .. #a3742c  (accent sobre fundo claro)
 *   dourado ........ #d5a253  (accent sobre fundo escuro / filetes)
 *   fundo suave .... #faf6f1  (seções alternadas)
 *   CTA ............ verde esmeralda built-in (emerald-*)
 */

function SparkleIcon({
  className = "",
  color = "#a3742c",
}: {
  className?: string;
  color?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill={color}
      aria-hidden="true"
      className={className}
    >
      <path d="M12 2c.65 4.9 2.5 7.55 10 10-7.5 2.45-9.35 5.1-10 10-.65-4.9-2.5-7.55-10-10 7.5-2.45 9.35-5.1 10-10z" />
    </svg>
  );
}

function CheckIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <circle cx="12" cy="12" r="11" fill="#059669" />
      <path
        d="M7 12.5l3.2 3.2L17 9"
        stroke="#ffffff"
        strokeWidth="2.4"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CrossIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <circle cx="12" cy="12" r="11" fill="#8a1f1f" />
      <path
        d="M8.5 8.5l7 7M15.5 8.5l-7 7"
        stroke="#ffffff"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChevronIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M5 7.5l5 5 5-5"
        stroke="#a3742c"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Eyebrow({
  children,
  onDark = false,
}: {
  children: React.ReactNode;
  onDark?: boolean;
}) {
  return (
    <p
      className="text-xs md:text-sm font-semibold uppercase tracking-[0.22em] mb-3"
      style={{ color: onDark ? "#d5a253" : "#a3742c" }}
    >
      {children}
    </p>
  );
}

function CtaButton({
  ariaLabel,
  onClick,
  title,
  subtitle,
}: {
  ariaLabel: string;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  title: string;
  subtitle?: string;
}) {
  return (
    <Link
      href="/form"
      aria-label={ariaLabel}
      onClick={onClick}
      className="cta-press block w-full max-w-md mx-auto rounded-2xl px-6 py-4 md:py-5 text-center text-lg md:text-xl font-bold text-white font-subtitle active:scale-[0.98]"
      style={{
        background:
          "linear-gradient(180deg, #10b981 0%, #059669 55%, #047857 100%)",
        boxShadow:
          "0 10px 24px -6px rgba(4,120,87,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
      }}
    >
      <span className="flex flex-col items-center justify-center leading-tight">
        <span>{title}</span>
        {subtitle && (
          <span className="mt-1.5 text-xs md:text-sm font-medium tracking-normal text-emerald-50/95">
            {subtitle}
          </span>
        )}
      </span>
    </Link>
  );
}

export default function HomeClientEng() {
  const [priceDisplay, setPriceDisplay] = useState<string>("US$ 33.00");
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [priceCents, setPriceCents] = useState<number>(
    config.pricing.fallback.priceCents,
  );
  const [standardDeliveryMinutes, setStandardDeliveryMinutes] = useState(120);
  const standardDeliveryTime = useMemo(() => {
    const hours = Math.floor(standardDeliveryMinutes / 60);
    const minutes = standardDeliveryMinutes % 60;
    if (hours && minutes) return `${hours} ${hours === 1 ? "hour" : "hours"} and ${minutes} minutes`;
    if (hours) return `${hours} ${hours === 1 ? "hour" : "hours"}`;
    return `${minutes} minutes`;
  }, [standardDeliveryMinutes]);
  const clientReady = useClientReady();
  const isLandingSafeMode = clientReady &&
    (isRuntimeSafeModeEnabled() || isInstagramInApp());
  const [priceValidUntil] = useState(() =>
    new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString().split("T")[0],
  );
  // Shared CTA click handler: preserves UTMs and attempts external open in in-app browsers
  const handleCtaClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    basePath: string = "/form",
    sectionId: string = "unknown",
    ctaId: string = "primary",
  ) => {
    try {
      // Always target the canonical public domain for external opens
      const CANONICAL_BASE =
        process.env.NEXT_PUBLIC_CANONICAL_BASE_URL ||
        getBaseUrlByLanguage("en-US");
      // Force path to /form if empty or root
      const effectiveBase = basePath && basePath !== "/" ? basePath : "/form";
      const path = effectiveBase.startsWith("http")
        ? basePath
        : effectiveBase.startsWith("/")
          ? effectiveBase
          : `/${effectiveBase}`;
      const utm = collectUtmFromLocation();
      const absolute = new URL(path, CANONICAL_BASE).toString();
      const target = appendUtmToUrl(absolute, utm);
      void trackLandingCtaClick({
        pageVariant: "en-US",
        sectionId,
        ctaId,
        targetUrl: target,
      });
      // Always prevent default so we control navigation to the canonical domain
      e.preventDefault();
      // External-open attempt disabled to reduce friction in in-app browsers.
      // Always navigate in the current browser context.
      window.location.href = target;
    } catch {
      /* no-op */
    }
  };

  useEffect(() => {
    const safeMode = isRuntimeSafeModeEnabled() || isInstagramInApp();
    addStartupMark("home_client_mode_detected", {
      page_variant: "en-US",
      safe_mode: safeMode,
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    const safeMode = isRuntimeSafeModeEnabled() || isInstagramInApp();
    (async () => {
      addStartupMark("home_client_boot_start", {
        page_variant: "en-US",
        safe_mode: safeMode,
      });
      try {
        await TarotApiService.autoLogin();
        addStartupMark("home_client_auto_login_success");
      } catch {
        /* ignore auth error */
        addStartupMark("home_client_auto_login_failed");
      }

      // Fire page view only after autoLogin attempt completes
      try {
        // Try to recover email/phone from sessionStorage to improve match quality
        const storedEmail =
          typeof window !== "undefined"
            ? sessionStorage.getItem("user_email") ||
              sessionStorage.getItem("mp_payer_email")
            : null;
        const storedPhone =
          typeof window !== "undefined"
            ? sessionStorage.getItem("user_phone")
            : null;
        // On Instagram iOS in-app, delay telemetry a bit to reduce startup race conditions.
        const runTrackPageView = () => {
          void trackPageView({
            email: storedEmail || undefined,
            phone: storedPhone || undefined,
          });
        };
        if (safeMode) {
          window.setTimeout(runTrackPageView, 700);
        } else {
          runTrackPageView();
        }
        addStartupMark("home_client_pageview_scheduled", {
          delayed_ms: safeMode ? 700 : 0,
        });
      } catch {
        /* ignore analytics error */
      }

      try {
        const p = safeMode
          ? await new Promise<
              Awaited<ReturnType<typeof pricingService.getPricingConfig>>
            >((resolve, reject) => {
              window.setTimeout(() => {
                pricingService.getPricingConfig().then(resolve).catch(reject);
              }, 250);
            })
          : await pricingService.getPricingConfig();
        if (!mounted) return;
        // Format price as USD
        if (typeof p.price_cents === "number") {
          setPriceCents(p.price_cents);
          setPriceDisplay(`US$ ${(p.price_cents / 100).toFixed(2)}`);
        }
        if (typeof p.standard_delivery_minutes === "number") {
          setStandardDeliveryMinutes(p.standard_delivery_minutes);
        }
        addStartupMark("home_client_pricing_loaded");
      } catch {
        /* keep defaults */
        addStartupMark("home_client_pricing_failed");
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // Removed separate trackPageView effect to avoid race with autoLogin

  // Structured data (JSON-LD) with dynamic price when available
  const jsonLd = useMemo(() => {
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "Channeled Letter",
      description:
        "Personalized channeled letter that reveals feelings and deep messages through an exclusive method (Hidden Mirror Code).",
      brand: {
        "@type": "Brand",
        name: "Channeled Letter",
      },
      image: [
        // Static image reference as SEO fallback (poster)
        `${getBaseUrlByLanguage("en-US")}/images/en-US/produto-final.jpg`,
      ],
      offers: {
        "@type": "Offer",
        priceCurrency: "USD",
        price: (priceCents / 100).toFixed(2),
        availability: "https://schema.org/InStock",
        url: `${getBaseUrlByLanguage("en-US")}/`,
        priceValidUntil,
      },
    };
  }, [priceCents, priceValidUntil]);

  return (
    <>
      {/* InAppBrowserBanner intentionally disabled to preserve funnel conversion */}
      <script
        type="application/ld+json"
        // Using dangerouslySetInnerHTML to inject JSON-LD
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <style jsx global>{`
        .section-transition {
          opacity: 1;
          transform: none;
          transition: none;
        }

        .landing-safe-mode .animate-pulse {
          animation: none !important;
        }

        .landing-safe-mode [class*="transition"] {
          transition: none !important;
        }

        /* Micro-interações da landing: o globals.css desliga transition/animation
           globalmente (estabilidade em in-app browsers); estas regras, mais
           específicas, reativam apenas o essencial fora do safe mode. */
        .landing-motion .cta-press {
          transition: transform 0.15s ease !important;
        }

        .landing-motion .faq-chevron {
          transition: transform 0.25s ease !important;
        }

        @media (prefers-reduced-motion: reduce) {
          .landing-motion .cta-press,
          .landing-motion .faq-chevron {
            transition: none !important;
          }
        }
      `}</style>
      <div
        className={`font-subtitle text-foreground${
          isLandingSafeMode ? " landing-safe-mode" : " landing-motion"
        }`}
      >
        {/* Hero Section */}
        <section
          className="section-transition relative overflow-hidden py-14 md:py-16 px-6"
          data-reveal-section
          data-debug="hero-section-inline"
          style={{
            background:
              "radial-gradient(120% 55% at 50% 0%, rgba(213,162,83,0.12) 0%, rgba(213,162,83,0) 60%), #ffffff",
          }}
        >
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="w-full mb-6">
                  <Image
                    src="/images/en-US/logo.webp"
                    alt="Product logo - Channeled Letter"
                    width={1040}
                    height={489}
                    priority
                    sizes="(max-width: 640px) 180px, (max-width: 768px) 220px, 260px"
                    className="block h-auto w-[180px] sm:w-[220px] md:w-[260px] object-contain"
                  />
                </div>

                <h1 className="font-title text-[1.9rem] leading-[1.2] md:text-5xl md:leading-tight mb-4 text-[#5c1616]">
                  Receive a channeled letter with everything he feels but never
                  had the courage to say.
                </h1>

                <p className="text-lg leading-relaxed mb-4 text-[#4a3230]">
                  Feel the emotion of receiving what his heart has kept in
                  silence until now.
                </p>
                <p className="text-[13px] md:text-sm italic leading-relaxed mb-6 text-[#8a6a5f]">
                  Individually written, based on tarot symbolism and intuitive
                  guidance.
                </p>

                <ul className="mb-8 space-y-3.5">
                  {[
                    "Receive your digital letter by email",
                    "Get a clear answer to your question, guided by a Tarot reading, plus a special message for you",
                    "Carefully prepared and delivered to your email in up to 4 hours",
                  ].map((txt, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckIcon className="mt-0.5 h-[22px] w-[22px] flex-shrink-0" />
                      <span className="font-medium leading-snug text-[#4a3230]">
                        {txt}
                      </span>
                    </li>
                  ))}
                </ul>

                <CtaButton
                  ariaLabel={`Start now for ${priceDisplay}`}
                  onClick={(e) =>
                    handleCtaClick(e, "/form", "hero", "hero-main")
                  }
                  title="Start My Personalized Reading"
                />
              </div>

              <div className="flex justify-center">
                <div className="relative w-full max-w-[600px]">
                  <div className="relative overflow-hidden rounded-2xl ring-1 ring-black/5 shadow-[0_16px_40px_-12px_rgba(92,22,22,0.25)]">
                    <Image
                      src="/images/en-US/produto-principal.webp"
                      alt="Product demonstration - Channeled Letter"
                      width={409}
                      height={614}
                      sizes="(max-width: 600px) 100vw, 600px"
                      className="block w-full h-auto"
                    />
                  </div>

                  {/* Price Block */}
                  <div className="mt-5">
                    <div
                      className="rounded-2xl bg-white p-5 md:p-6 text-center"
                      style={{
                        border: "1px solid rgba(213,162,83,0.35)",
                        boxShadow: "0 14px 34px -10px rgba(92,22,22,0.18)",
                      }}
                    >
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] md:text-xs font-semibold uppercase tracking-[0.18em] text-[#a3742c]"
                        style={{ background: "rgba(213,162,83,0.12)" }}
                      >
                        <SparkleIcon className="h-3 w-3" />
                        Personalized Reading
                      </span>
                      <div className="mt-3 mb-4">
                        <span className="text-[44px] leading-none font-bold text-emerald-700">
                          {priceDisplay}
                        </span>
                      </div>
                      <div className="mb-5 flex flex-col items-center gap-1.5 text-sm text-[#8a6a5f]">
                        <span className="inline-flex items-center gap-2">
                          <CheckIcon className="h-4 w-4 flex-shrink-0" />
                          One-time payment
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <CheckIcon className="h-4 w-4 flex-shrink-0" />
                          Secure checkout
                        </span>
                      </div>
                      <CtaButton
                        ariaLabel={`Start my reading securely for ${priceDisplay}`}
                        onClick={(e) =>
                          handleCtaClick(
                            e,
                            "/form",
                            "hero-price",
                            "hero-price-main",
                          )
                        }
                        title="Start My Reading Securely"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Seção do Problema/Contexto */}
        <section
          className="section-transition relative overflow-hidden py-16 px-6"
          data-reveal-section
          data-debug="bordo-section-problema-inline"
          style={{
            background: "linear-gradient(180deg, #451515 0%, #2b0d0d 100%)",
            borderTop: "1px solid rgba(213,162,83,0.3)",
            borderBottom: "1px solid rgba(213,162,83,0.3)",
          }}
        >
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center max-w-3xl mx-auto">
              <div
                data-debug="receber-oportunidade-panel"
                className="whitespace-pre-line leading-relaxed py-2 text-[#f6e3da]"
              >
                {`Late at night, when everything gets quiet, your mind starts replaying the moments you try so hard to ignore.
The song that reminds you of him.
The message you almost sent.
The questions that never stop echoing inside your chest.

What hurts isn't the silence itself. It's not knowing what hides behind it.

The Channeled Letter uses tarot and intuitive reading to bring clarity to the emotions he struggles to express.

It doesn't promise answers he never gave, but it gently reveals the emotional truth behind his silence. It shows the part you always felt but could never put into words.`}
              </div>
            </div>

            <div className="text-center max-w-3xl mx-auto">
              <div
                className="my-8 rounded-2xl bg-[#fdf8f3] p-7 md:p-8 text-center"
                style={{
                  border: "1px solid rgba(213,162,83,0.45)",
                  boxShadow: "0 22px 48px -16px rgba(0,0,0,0.5)",
                }}
              >
                <h3 className="text-xl md:text-2xl font-subtitle font-bold uppercase tracking-wide mb-3 text-[#5c1616]">
                  What truly works:
                </h3>
                <div className="whitespace-pre-line leading-relaxed text-[#4a3230]">
                  {`While you're trying to make sense of mixed signals, many women are already discovering something different, a message that brings clarity instead of confusion.

Imagine waking up to a letter that feels emotionally honest, like someone finally putting into words the feelings you always sensed but could never fully understand.

Hidden Voice of Love is a tarot-guided intuitive reading that transforms silence into understanding.
It doesn't guess. It interprets the emotional tone behind his actions, revealing what lies beneath the surface in a way only you can feel.`}
                </div>

                <div className="pt-6">
                  <div
                    className="inline-flex flex-wrap items-center justify-center gap-2 rounded-full px-4 py-2 mx-auto"
                    style={{
                      background: "rgba(213,162,83,0.14)",
                      border: "1px solid rgba(163,116,44,0.45)",
                    }}
                  >
                    <SparkleIcon className="h-4 w-4 flex-shrink-0" />
                    <h3 className="text-base md:text-lg font-subtitle font-bold tracking-wide text-[#5c1616]">
                      Presenting the Hidden Voice of Love
                    </h3>
                    <SparkleIcon className="h-4 w-4 flex-shrink-0" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Seção Sobre o Produto */}
        <section
          className="section-transition relative overflow-hidden py-16 px-6"
          data-reveal-section
          data-debug="sobre-section-plain"
          style={{ background: "#ffffff" }}
        >
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <Eyebrow>ABOUT THIS SERVICE</Eyebrow>
                <h2 className="font-title text-3xl md:text-4xl leading-snug mb-4 text-[#5c1616]">
                  Channeled Letter: When Tarot translates the voice of the one
                  you love.
                </h2>
                <p className="text-lg leading-relaxed mb-6 text-[#4a3230]">
                  There is a quiet way to understand everything he feels... Even
                  when silence seems to say nothing. Amid tangled emotions and
                  confused thoughts, the truth hides, waiting to be revealed.
                  Through tarot, this millenary oracular tool, the veil is
                  lifted. Card by card, the shadows dissipate... and the truth,
                  once lost between the lines of the heart, finally comes to
                  light.
                </p>

                <div
                  className="my-8 rounded-2xl bg-[#faf6f1] p-7 md:p-8 text-center"
                  style={{
                    border: "1px solid rgba(213,162,83,0.35)",
                    boxShadow: "0 14px 34px -12px rgba(92,22,22,0.14)",
                  }}
                >
                  <h3 className="flex flex-wrap items-center justify-center gap-2 text-xl md:text-2xl font-subtitle font-bold uppercase tracking-wide mb-3 text-[#5c1616]">
                    <SparkleIcon className="h-4 w-4 flex-shrink-0" />
                    Our Guarantee For You
                    <SparkleIcon className="h-4 w-4 flex-shrink-0" />
                  </h3>
                  <p className="text-base leading-7 text-[#4a3230]">
                    Your request will be handled with care, respect, and within
                    the agreed timeframe. If your letter is not delivered within
                    {standardDeliveryTime}, or if the delivery does not match what was
                    presented on this page, you can contact us within 48 hours
                    after receiving it.
                    <br />
                    <br />
                    In those cases, we will review everything carefully and, if
                    the issue is confirmed, we will refund 100% of the amount
                    paid, with no bureaucracy.
                  </p>
                </div>

                <CtaButton
                  ariaLabel={`Secure now for ${priceDisplay}`}
                  onClick={(e) =>
                    handleCtaClick(e, "/form", "about-service", "about-main")
                  }
                  title="I Want My Letter Now"
                />
              </div>

              <div className="flex justify-center">
                <div className="relative w-full max-w-[600px]">
                  <div className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-black/5 shadow-[0_16px_40px_-12px_rgba(92,22,22,0.2)]">
                    <Image
                      src="/images/produto-final.jpg"
                      alt="Final product mockup - Channeled Letter"
                      width={651}
                      height={488}
                      priority
                      sizes="(max-width: 600px) 100vw, 600px"
                      className="block h-auto w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Como Funciona o Método */}
        <section
          className="section-transition relative overflow-hidden text-center py-16 px-6"
          data-reveal-section
          data-theme="bordo"
          style={{
            background: "#faf6f1",
            borderTop: "1px solid rgba(213,162,83,0.25)",
            borderBottom: "1px solid rgba(213,162,83,0.25)",
          }}
        >
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-title text-3xl md:text-4xl mb-2 text-[#5c1616]">
              How the Method Works
            </h2>
            <h3 className="text-xl md:text-2xl font-subtitle font-bold tracking-wide mb-8 text-[#a3742c]">
              Hidden Voice of Love
            </h3>
            <div className="text-lg leading-relaxed mb-12 max-w-4xl mx-auto text-[#4a3230]">
              <div className="whitespace-pre-line">
                {`Hidden Voice of Love is a guided intuitive reading that combines tarot symbolism with emotional interpretation to reveal the deeper meaning behind someone's silence.

Instead of trying to guess what he feels or reading between the lines, you receive a letter shaped with clarity, written in a tone that resonates with the connection you share.

Each message brings forward the emotional context behind his actions and unspoken words.
Not predictions.
Not assumptions.
Just a deeper, more honest view of what exists underneath the surface.

While others try to make sense of uncertainty, you receive a message crafted with intention, presence, and intuitive understanding. A moment of clarity translated into words.`}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 max-w-[800px] mx-auto mb-4">
              {[
                {
                  label: "Start (creation)",
                  src: "/images/metodo-passo-1.webp",
                  description:
                    "The cards reveal the energy and truth of the moment.",
                },
                {
                  label: "Middle (application)",
                  src: "/images/metodo-passo-2.webp",
                  description:
                    "The message is channeled and written with intention and care.",
                },
                {
                  label: "End (results)",
                  src: "/images/metodo-passo-3.webp",
                  description: "The message reaches you at the right moment.",
                },
              ].map((item, i) => (
                <div key={i} className="flex flex-col">
                  <div className="relative aspect-[4/5] md:aspect-[22/21] overflow-hidden rounded-2xl ring-1 ring-black/5 shadow-[0_12px_30px_-12px_rgba(92,22,22,0.22)]">
                    <Image
                      src={item.src}
                      alt={`Method step - ${item.label}`}
                      fill
                      sizes="(max-width: 768px) 100vw, 33vw"
                      className="object-cover"
                    />
                  </div>
                  <p className="mt-3 text-sm md:text-base leading-relaxed text-center text-[#8a6a5f]">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Quem escreve sua carta */}
        <section
          className="relative overflow-hidden py-14 px-6"
          style={{ background: "#ffffff" }}
        >
          <div className="max-w-3xl mx-auto">
            <div
              className="rounded-2xl bg-white p-8 md:p-10"
              style={{
                border: "1px solid rgba(213,162,83,0.35)",
                boxShadow: "0 16px 38px -14px rgba(92,22,22,0.16)",
              }}
            >
              <h3 className="font-title text-2xl md:text-3xl mb-5 text-[#5c1616]">
                Who Writes Your Letter?
              </h3>
              <p className="text-lg leading-relaxed mb-4 text-[#4a3230]">
                Each Channeled Letter is personally written by a member of our
                intuitive team, with experience in tarot interpretation and
                emotional guidance.
              </p>
              <p className="text-lg leading-relaxed text-[#4a3230]">
                Every message is crafted individually, with presence and
                intention - never mass-produced.
              </p>
            </div>
          </div>
        </section>
        <section
          className="section-transition relative overflow-hidden py-16 px-6"
          data-reveal-section
          style={{ background: "#faf6f1" }}
        >
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <h2 className="font-title text-3xl md:text-[44px] md:leading-tight mb-3 text-[#5c1616]">
                Real Women. Real Messages. Real Closure.
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-[#8a6a5f]">
                Over 1,200 women have already experienced their channeled
                letter.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                "/images/en-US/Depoimento 1.jpg",
                "/images/en-US/Depoimento 2.jpg",
                "/images/en-US/Depoimento 3.jpg",
                "/images/en-US/Depoimento 4.jpg",
                "/images/en-US/Depoimento 5.jpg",
              ].map((src, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-white p-2 ring-1 ring-black/5 shadow-[0_12px_30px_-12px_rgba(92,22,22,0.18)]"
                >
                  <div className="relative aspect-[4/5] overflow-hidden rounded-xl">
                    <Image
                      src={src}
                      alt={`Testimonial ${i + 1}`}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      className="object-cover"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <CtaButton
                ariaLabel={`Receive my letter now for ${priceDisplay}`}
                onClick={(e) =>
                  handleCtaClick(
                    e,
                    "/form",
                    "testimonials",
                    "testimonials-main",
                  )
                }
                title="Receive My Letter Now"
              />
            </div>
          </div>
        </section>
        {/* Empilhamento de Valor / Pricing */}
        <section
          className="section-transition relative overflow-hidden py-16 px-6 text-center"
          data-reveal-section
          data-debug="receber-section-plain"
          style={{ background: "#ffffff" }}
        >
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-title text-3xl md:text-4xl mb-10 text-[#5c1616]">
              See Everything You&apos;ll Receive
            </h2>
            <div
              className="max-w-2xl mx-auto rounded-2xl bg-white p-6 md:p-8"
              style={{
                border: "1px solid rgba(213,162,83,0.35)",
                boxShadow: "0 18px 44px -14px rgba(92,22,22,0.18)",
              }}
            >
              <div className="text-left mb-8 space-y-6">
                {[
                  {
                    title: "Personalized Channeled Letter",
                    description:
                      "A profound message revealing feelings, thoughts, and unspoken truths.",
                  },
                  {
                    title: "Direct Answer to Your Question",
                    description:
                      "Clear guidance on your specific question, guided by a Tarot reading.",
                  },
                  {
                    title: "Special Tarot Guidance",
                    description:
                      "An extra message to illuminate your moment and your next steps.",
                  },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <CheckIcon className="mt-1 h-6 w-6 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-lg leading-relaxed text-[#4a3230]">
                        {item.title}
                      </span>
                      <p className="text-sm mt-1 leading-relaxed text-[#8a6a5f]">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}

                <div
                  className="mt-8 pt-6"
                  style={{ borderTop: "1px solid rgba(213,162,83,0.35)" }}
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-bold text-lg md:text-xl text-[#4a3230]">
                      Personalized Reading
                    </span>
                    <span className="text-emerald-700 text-3xl font-bold">
                      {priceDisplay}
                    </span>
                  </div>
                </div>
              </div>
              <CtaButton
                ariaLabel={`Receive my channeled letter for ${priceDisplay}`}
                onClick={(e) =>
                  handleCtaClick(e, "/form", "opportunity", "opportunity-main")
                }
                title="Receive My Channeled Letter"
              />
              <div className="mt-8 text-center">
                <div
                  data-debug="receber-oportunidade-panel"
                  className="whitespace-pre-line leading-relaxed rounded-2xl bg-[#faf6f1] p-6 text-[#4a3230]"
                  style={{ border: "1px solid rgba(213,162,83,0.3)" }}
                >
                  {`Because each letter is created individually, with focus, intuition, and emotional presence, only a limited number can be delivered each day.

If the start button is available now, it means we still have space for your reading at this moment.

This price is reserved for those who feel aligned with this experience and trust their intuition enough to take the next step with ease.

If your heart has been asking for a sign...
this may be the moment to listen.`}
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Comparativo de Ganhos vs Perdas */}
        <section
          className="section-transition relative overflow-hidden py-16 px-6"
          data-reveal-section
          data-debug="compare-section-inline"
          style={{ background: "#ffffff" }}
        >
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-title text-3xl md:text-4xl text-center mb-10 text-[#5c1616]">
              See The Difference This Will Make In Your Life
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
              {/* Coluna AGIR */}
              <div
                className="rounded-2xl bg-white p-6 md:p-7"
                style={{
                  border: "1px solid rgba(5,150,105,0.25)",
                  boxShadow: "0 16px 38px -14px rgba(4,120,87,0.25)",
                }}
              >
                <h3 className="flex items-center justify-center gap-2 text-xl md:text-2xl font-subtitle font-bold text-center mb-5 text-emerald-700">
                  <CheckIcon className="h-6 w-6 flex-shrink-0" />
                  IF YOU ACT TODAY
                </h3>
                <ul className="list-none space-y-4">
                  {[
                    "Receive words that break the silence and reveal the love he still holds inside.",
                    "Understand what's in his heart without needing to ask the questions you're afraid to voice.",
                    "See the truth he hides-even from himself-as if the universe were opening a window just for you.",
                    "Finally gain clarity, peace, and emotional confidence about where you stand in his life.",
                  ].map((txt, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckIcon className="mt-0.5 h-5 w-5 flex-shrink-0" />
                      <span className="font-medium leading-relaxed text-[#4a3230]">
                        {txt}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Coluna NÃO AGIR */}
              <div
                className="rounded-2xl bg-white p-6 md:p-7"
                style={{
                  border: "1px solid rgba(92,22,22,0.22)",
                  boxShadow: "0 16px 38px -14px rgba(92,22,22,0.22)",
                }}
              >
                <h3 className="flex items-center justify-center gap-2 text-xl md:text-2xl font-subtitle font-bold text-center mb-5 text-[#8a1f1f]">
                  <CrossIcon className="h-6 w-6 flex-shrink-0" />
                  IF YOU DON&apos;T ACT
                </h3>
                <ul className="list-none space-y-4">
                  {[
                    "Keep trying to decode mixed signals and emojis that never tell you what you really need to know.",
                    "Fall asleep wondering if he thinks about you, while pretending you don't think about him.",
                    "Stay stuck in uncertainty, replaying stories and signs that only make you more confused.",
                    "Continue feeling invisible in a connection that could finally make sense with real guidance.",
                  ].map((txt, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CrossIcon className="mt-0.5 h-5 w-5 flex-shrink-0" />
                      <span className="font-medium leading-relaxed text-[#4a3230]">
                        {txt}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
        {/* Garantia Final */}
        <section
          data-debug="garantia-section-inline"
          className="section-transition relative overflow-hidden py-16 md:py-20 px-6"
          data-reveal-section
          style={{
            background: "#faf6f1",
            borderTop: "1px solid rgba(213,162,83,0.25)",
            borderBottom: "1px solid rgba(213,162,83,0.25)",
          }}
        >
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-title text-3xl md:text-4xl text-center mb-8 text-[#5c1616]">
              Our Commitment to You
            </h2>
            <div className="max-w-3xl mx-auto">
              <div
                className="rounded-2xl bg-white p-6 md:p-10 mb-10"
                style={{
                  border: "1px solid rgba(213,162,83,0.35)",
                  boxShadow: "0 16px 38px -14px rgba(92,22,22,0.16)",
                }}
              >
                <p className="text-base md:text-lg leading-relaxed whitespace-pre-line text-left text-[#4a3230]">
                  {`After everything you've seen here, the testimonials, the process, the intention behind each letter, it's completely natural if a part of you still wants one more sign of reassurance.

And I respect that deeply.

That's why I offer something simple, real, and completely transparent:

Your Channeled Letter is a handcrafted experience, created personally for you with care, intuition, and dedication.
If the delivery does not match what was promised in quality, clarity, or timing, just contact us within 7 days.

We will refund 100% of your payment.

No stress.
No complications.
No explanations required.

This guarantee exists because trust is more than a word.
It's an agreement between us, that you will receive something meaningful, intentional, and delivered with integrity.

If for any reason that doesn't happen, you are fully protected.

Your peace of mind matters.
And that, I guarantee.`}
                </p>
              </div>
              <CtaButton
                ariaLabel={`Secure now with full protection for ${priceDisplay}`}
                onClick={(e) =>
                  handleCtaClick(e, "/form", "guarantee", "guarantee-main")
                }
                title="Request My Letter With Full Protection"
              />
            </div>
          </div>
        </section>
        {/* FAQ */}
        <section
          data-debug="faq-section-inline"
          className="section-transition relative overflow-hidden py-16 px-6"
          data-reveal-section
          style={{ background: "#ffffff" }}
        >
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-title text-3xl md:text-4xl text-center mb-10 text-[#5c1616]">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {[
                {
                  q: "Is it safe to order my Channeled Letter?",
                  a: (
                    <>
                      <p>
                        Yes. You can verify the security of your order through
                        clear, publicly available trust signals:
                      </p>
                      <ul className="mt-3 list-disc space-y-2 pl-5">
                        <li>
                          our official Instagram profile is Meta Verified, with
                          daily posts, thousands of followers, and hundreds of
                          comments;
                        </li>
                        <li>
                          the service is provided by a legally registered
                          business, with policies and legal information available
                          on this website;
                        </li>
                        <li>
                          payments are processed by Stripe, which securely
                          handles the transaction.
                        </li>
                      </ul>
                    </>
                  ),
                },
                {
                  q: "Can I access it on my phone?",
                  a: "Yes! The entire experience is delivered digitally and the platform is 100% responsive. You can access it from any device.",
                },
                {
                  q: "How do I receive my channeled letter?",
                  a: "After your payment is confirmed, your personalized letter is carefully prepared and delivered to your email in up to 4 hours.",
                },
                {
                  q: "Who will have access to my data?",
                  a: "No one will have access to your contact data. The order data will be seen only by the tarot reader who will take care of your service. All data is stored temporarily during the service and is automatically deleted afterwards.",
                },
                {
                  q: "Is the letter really personalized?",
                  a: "Yes. Each reading is unique, crafted by one of our tarot readers using our exclusive channeling-inspired method.",
                },
                {
                  q: "What if I don't like what I receive?",
                  a: "We trust our work so much that we offer a 7-day guarantee: if you feel it's not what you expected, we refund your investment.",
                },
                {
                  q: "Does it work even if he/she is distant or not talking to me?",
                  a: "Yes. The channeling accesses the person's emotional energy, regardless of the distance or the current situation between you.",
                },
              ].map((item, i) => {
                const isOpen = openFaq === i;
                const panelId = `faq-panel-${i}`;
                return (
                  <div
                    key={i}
                    className="rounded-xl overflow-hidden bg-white ring-1 ring-black/5 shadow-[0_6px_18px_-8px_rgba(92,22,22,0.15)]"
                  >
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full text-left px-5 py-4 font-subtitle font-semibold flex items-center justify-between gap-3 text-[#5c1616]"
                    >
                      <span>{item.q}</span>
                      <ChevronIcon
                        className={`faq-chevron h-5 w-5 flex-shrink-0${
                          isOpen ? " rotate-180" : ""
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div
                        id={panelId}
                        className="px-5 pb-5 pt-4 leading-7 text-[#4a3230]"
                        style={{
                          borderTop: "1px solid rgba(213,162,83,0.25)",
                        }}
                      >
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
